'use client'

import { Button } from '@/components/ui/button'
import { ArrowRight, Smartphone, Mail } from 'lucide-react'
import { useState } from 'react'

const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL?.replace(/\/$/, '') ?? ''

const getApiUrl = (path: string) => {
  return serverUrl ? `${serverUrl}${path}` : path
}

export function CTA() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || loading) {
      return
    }

    setError('')
    setLoading(true)

    try {
      const response = await fetch(getApiUrl('/api/preregister'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        let message = 'Unable to save your preregistration. Please try again.'

        try {
          const data = await response.json()
          if (typeof data?.message === 'string' && data.message.trim()) {
            message = data.message
          }
        } catch {
          // Keep the default error message when the response is not JSON.
        }

        throw new Error(message)
      }

      setSubmitted(true)
      setEmail('')
      setTimeout(() => setSubmitted(false), 3000)
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Unable to save your preregistration. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section id="preregister" className="w-full py-20 md:py-28 bg-gradient-to-b from-white to-muted/20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex flex-col items-center text-center gap-10">
          {/* Main CTA */}
          <div className="space-y-6 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/15 border border-secondary/30">
              <Smartphone className="w-4 h-4 text-secondary" />
              <span className="text-sm font-bold text-secondary">Android Exclusive Pre-Registration</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold text-foreground text-balance">
              Be first to experience Alarvia
            </h2>
            
            <p className="text-xl text-muted-foreground">
              Pre-register now for exclusive early access and get your first month free when Alarvia launches on Android.
            </p>
          </div>

          {/* Email signup */}
          <form onSubmit={handleSubmit} className="w-full max-w-md flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-border/50 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition bg-white"
                  required
                />
              </div>
              <Button 
                type="submit"
                disabled={loading || !email}
                className="bg-primary hover:bg-accent text-white font-bold text-lg h-12 px-8 group whitespace-nowrap"
              >
                {loading ? 'Saving...' : submitted ? 'Thanks!' : 'Pre-Register'}
                {!loading && !submitted && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition" />}
              </Button>
            </div>
            {error && (
              <p className="text-sm text-destructive font-medium text-center">
                {error}
              </p>
            )}
            {submitted && (
              <p className="text-sm text-green-600 font-medium text-center">
                ✓ Great! You&apos;re on the preregister list for one month free.
              </p>
            )}
          </form>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-8 pt-8 border-t border-muted w-full">
            <div className="flex flex-col items-center gap-2">
              <span className="text-2xl font-bold text-primary">Early Access</span>
              <span className="text-sm text-muted-foreground">Launch day release</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-2xl font-bold text-secondary">1 Month Free</span>
              <span className="text-sm text-muted-foreground">Pre-registration offer</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-2xl font-bold text-accent">Lifetime Updates</span>
              <span className="text-sm text-muted-foreground">Always improving</span>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-sm text-muted-foreground">
            Android exclusive launch. Coming to iOS later in 2026.
          </p>
        </div>
      </div>
    </section>
  )
}
